import dayjs from 'dayjs'
import type { UsersReportSearchLogRow } from '../interfaces'
import { findUserLabelByUrl } from './reportTypeOptions'

// ---------------------------------------------------------------------------
// TIPOS
// ---------------------------------------------------------------------------

export type ModalField = {
  readonly label: string
  readonly value: string | number | null | React.ReactNode
  readonly size?: number
}

export type UserDetails = UsersReportSearchLogRow

// ---------------------------------------------------------------------------
// DETECÇÃO DE AÇÕES
// ---------------------------------------------------------------------------

export function isCreateUser(details: UserDetails): boolean {
  return details.route === '/user/store'
}

export function isEditUser(details: UserDetails): boolean {
  const normalizedRoute = details.route?.replace(/\/\d+/g, '/:id')
  return normalizedRoute === '/user/:id' && details.request_type === 'PUT'
}

export function isActivateDeactivateUser(details: UserDetails): boolean {
  const normalizedRoute = details.route?.replace(/\/\d+/g, '/:id')
  return normalizedRoute === '/user/:id/activate-or-deactivate'
}

export function isChangePassword(details: UserDetails): boolean {
  return details.route === '/auth/login/change-password'
}

export function isDisconnectUser(details: UserDetails): boolean {
  return details.route === '/user/force-disconnection'
}

// ---------------------------------------------------------------------------
// HELPERS DE EXTRAÇÃO DE DADOS
// ---------------------------------------------------------------------------

function formatEmergencyContact(name?: string, phone?: string) {
  const unknown = 'Não informado'

  const cleanName = name && name !== unknown ? name : undefined
  const cleanPhone = phone && phone !== unknown ? phone : undefined

  if (!cleanName && !cleanPhone) return unknown
  if (cleanName && cleanPhone) return `${cleanName} — ${cleanPhone}`
  return cleanName ?? cleanPhone ?? unknown
}

function extractTargetUser(details: UserDetails, preferLogin = true) {
  if (preferLogin) {
    const loginObj =
      details?.login && typeof details.login !== 'string'
        ? details.login
        : undefined
    const userObj =
      details?.user && typeof details.user !== 'string'
        ? details.user
        : undefined
    return loginObj ?? userObj
  }
  const userObj =
    details?.user && typeof details.user !== 'string' ? details.user : undefined
  const loginObj =
    details?.login && typeof details.login !== 'string'
      ? details.login
      : undefined
  return userObj ?? loginObj
}

function extractUserLabel(
  details: UserDetails,
  target?: { readonly representation_name?: string; readonly login?: string }
): string {
  const anyTarget = target as Record<string, unknown> | undefined
  return (
    (anyTarget?.representation_name as string) ??
    (anyTarget?.login as string) ??
    details?.representation_name ??
    details['user:representation_name'] ??
    'Não informado'
  )
}

function formatDate(date?: string): string {
  return date ? dayjs(date).format('DD/MM/YYYY HH:mm') : 'Não informado'
}

export function formatGroupsList(groups: unknown): string {
  if (!groups) return 'Não informado'
  if (Array.isArray(groups)) {
    const groupNames = groups
      .map((group) => {
        if (typeof group === 'string') return group
        if (typeof group === 'object' && group !== null) {
          const obj = group as Record<string, unknown>
          return obj['name'] as string | undefined
        }
        return undefined
      })
      .filter(Boolean)

    return groupNames.length > 0 ? groupNames.join(', ') : 'Não informado'
  }
  return 'Não informado'
}

// ---------------------------------------------------------------------------
// CAMPOS BASE COMUNS
// ---------------------------------------------------------------------------

export function buildBaseUserFields(
  details: UserDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const actionLabel = details.route
    ? findUserLabelByUrl(details.route, translate)
    : undefined

  const target = extractTargetUser(details, false)
  const userName = extractUserLabel(details, target)

  return [
    { label: translate('User') || 'Usuário', value: userName },
    {
      label: translate('Action') || 'Ação',
      value:
        actionLabel ??
        details.action_display ??
        details['action'] ??
        'Não informado',
    },
    {
      label: translate('Performed at') || 'Realizado em',
      value: formatDate(details.created_at),
    },
  ]
}

// ---------------------------------------------------------------------------
// AGRUPAMENTO DE GRUPOS DE DESPACHO
// ---------------------------------------------------------------------------

export function categorizeDispatchGroups(
  details: UserDetails
): Record<string, readonly string[]> {
  if (!Array.isArray(details?.dispatch_groups)) return {}

  const categoryKeys = [
    'Polícia Militar',
    'Corpo de Bombeiros Militar',
    'BPTUR (Policiamento Turístico)',
    'Autarquia Municipal de Trânsito e Cidadania',
    'Polícia Civil',
    'Perícia Forense (COMEL)',
    'Outros',
  ] as const

  const classify = (input?: string | null) => {
    if (!input) return 'Outros'
    const valueUpper = String(input).toUpperCase()
    if (valueUpper.startsWith('PM')) return 'Polícia Militar'
    if (valueUpper.startsWith('CBM')) return 'Corpo de Bombeiros Militar'
    if (valueUpper.startsWith('BPTUR')) return 'BPTUR (Policiamento Turístico)'
    if (valueUpper.startsWith('AMC'))
      return 'Autarquia Municipal de Trânsito e Cidadania'
    if (valueUpper.startsWith('PC')) return 'Polícia Civil'
    if (valueUpper.startsWith('COMEL')) return 'Perícia Forense (COMEL)'
    return 'Outros'
  }

  const initial = Object.fromEntries(
    categoryKeys.map((key) => [key, [] as readonly string[]])
  ) as Record<string, readonly string[]>

  const reduced = details.dispatch_groups.reduce<
    Record<string, readonly string[]>
  >((acc, group) => {
    const display = (group?.name ?? group?.acronym ?? '').toString().trim()
    if (!display) return acc
    const category = classify(display)
    const existing = acc[category] ?? ([] as readonly string[])
    const next = existing.includes(display)
      ? existing
      : ([...existing, display] as readonly string[])
    return { ...acc, [category]: next }
  }, initial)

  return Object.fromEntries(
    Object.entries(reduced).map(([key, arr]) => [key, arr as readonly string[]])
  ) as Record<string, readonly string[]>
}

// ---------------------------------------------------------------------------
// HELPERS COMPARTILHADOS PARA CREATE/EDIT
// ---------------------------------------------------------------------------

function buildCommonUserFields(details: UserDetails) {
  const loginObj =
    details?.login && typeof details.login !== 'string'
      ? details.login
      : undefined

  const userDisplayName =
    details?.name ||
    loginObj?.representation_name ||
    details?.representation_name ||
    details['user:representation_name'] ||
    'Não informado'

  const nameField =
    details?.name ?? details?.representation_name ?? 'Não informado'
  const login =
    (loginObj?.login ??
      (typeof details?.login === 'string' ? details?.login : undefined)) ||
    'Não informado'
  const registration = details?.registration ?? 'Não informado'
  const email = details?.email ?? loginObj?.email ?? 'Não informado'
  const cpfFromLogin =
    loginObj &&
    (typeof loginObj.cpf === 'string' || typeof loginObj.cpf === 'number')
      ? String(loginObj.cpf)
      : undefined
  const cpf = details?.cpf ?? cpfFromLogin ?? 'Não informado'
  const birthDate = details?.birth_date
    ? dayjs(details.birth_date).format('DD/MM/YYYY')
    : 'Não informado'
  const phone = details?.phone ?? 'Não informado'
  const bloodType = details?.blood_type ?? 'Não informado'
  const nickname = details?.nickname ?? 'Não informado'
  const agency = details?.agency
    ? details.agency.name ?? 'Não informado'
    : 'Não informado'
  const rank = details?.rank
    ? details.rank.name ?? 'Não informado'
    : 'Não informado'
  const emergencyContactName =
    details?.emergency_contact_name ?? 'Não informado'
  const emergencyContactPhone =
    details?.emergency_contact_phone ?? 'Não informado'
  const permissions = Array.isArray(details?.groups)
    ? details.groups
        .map((group) => group.name)
        .filter(Boolean)
        .join(', ') || 'Não informado'
    : 'Não informado'
  const performedAt = formatDate(details.created_at)

  return {
    userDisplayName,
    nameField,
    login,
    registration,
    email,
    cpf,
    birthDate,
    phone,
    bloodType,
    nickname,
    agency,
    rank,
    emergencyContactName,
    emergencyContactPhone,
    permissions,
    performedAt,
  }
}

function buildDispatchGroupColumns(
  categorizedGroups: Record<string, readonly string[]>,
  translate: (key: string) => string
): readonly ModalField[] {
  const order = [
    'Polícia Militar',
    'Corpo de Bombeiros Militar',
    'BPTUR (Policiamento Turístico)',
    'Autarquia Municipal de Trânsito e Cidadania',
    'Polícia Civil',
    'Perícia Forense (COMEL)',
    'Outros',
  ] as const

  return order.flatMap((cat) => {
    const values = categorizedGroups[cat] ?? []
    if (!values || values.length === 0) return [] as readonly ModalField[]

    const size = cat === 'Outros' ? 12 : 6
    return [
      {
        label: translate(cat) || cat,
        value: values.join(', '),
        size,
      },
    ] as readonly ModalField[]
  })
}

// ---------------------------------------------------------------------------
// BUILDERS POR AÇÃO
// ---------------------------------------------------------------------------

export function buildCreateUserFields(
  details: UserDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const fields = buildCommonUserFields(details)
  const categorizedDispatchGroups = categorizeDispatchGroups(details)

  return [
    { label: translate('Usuário') || 'Usuário', value: fields.userDisplayName },
    { label: translate('Nome') || 'Nome', value: fields.nameField },
    { label: 'Login', value: fields.login },
    {
      label: translate('Matrícula') || 'Matrícula',
      value: fields.registration,
    },
    { label: translate('Email') || 'Email', value: fields.email },
    { label: translate('CPF') || 'CPF', value: fields.cpf },
    {
      label: translate('Data de Nascimento') || 'Data de Nascimento',
      value: fields.birthDate,
    },
    { label: translate('Telefone') || 'Telefone', value: fields.phone },
    {
      label: translate('Tipo Sanguíneo') || 'Tipo Sanguíneo',
      value: fields.bloodType,
    },
    {
      label: translate('Nome de Guerra') || 'Nome de Guerra',
      value: fields.nickname,
    },
    { label: translate('Agência') || 'Agência', value: fields.agency },
    { label: translate('Patente') || 'Patente', value: fields.rank },
    {
      label: translate('Contato de Emergência') || 'Contato de Emergência',
      value: formatEmergencyContact(
        fields.emergencyContactName,
        fields.emergencyContactPhone
      ),
      size: 3,
    },
    {
      label: translate('Permission Groups') || 'Permission Groups',
      value: fields.permissions,
      size: 3,
    },
    {
      label: translate('Performed at') || 'Realizado em',
      value: fields.performedAt,
    },
    {
      label: translate('Grupos de Despacho') || 'Grupos de Despacho',
      value: '',
      size: 12,
    },
    ...buildDispatchGroupColumns(categorizedDispatchGroups, translate),
  ]
}

export function buildEditUserFields(
  details: UserDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const fields = buildCommonUserFields(details)
  const categorizedDispatchGroups = categorizeDispatchGroups(details)

  return [
    { label: translate('Usuário') || 'Usuário', value: fields.userDisplayName },
    { label: translate('Nome') || 'Nome', value: fields.nameField },
    { label: 'Login', value: fields.login },
    {
      label: translate('Matrícula') || 'Matrícula',
      value: fields.registration,
    },
    { label: translate('Email') || 'Email', value: fields.email },
    { label: translate('CPF') || 'CPF', value: fields.cpf },
    {
      label: translate('Data de Nascimento') || 'Data de Nascimento',
      value: fields.birthDate,
    },
    { label: translate('Telefone') || 'Telefone', value: fields.phone },
    {
      label: translate('Tipo Sanguíneo') || 'Tipo Sanguíneo',
      value: fields.bloodType,
    },
    {
      label: translate('Nome de Guerra') || 'Nome de Guerra',
      value: fields.nickname,
    },
    { label: translate('Agência') || 'Agência', value: fields.agency },
    { label: translate('Patente') || 'Patente', value: fields.rank },
    {
      label: translate('Contato de Emergência') || 'Contato de Emergência',
      value: formatEmergencyContact(
        fields.emergencyContactName,
        fields.emergencyContactPhone
      ),
      size: 3,
    },
    {
      label: translate('Permission Groups') || 'Permission Groups',
      value: fields.permissions,
      size: 3,
    },
    {
      label: translate('Performed at') || 'Realizado em',
      value: fields.performedAt,
    },
    {
      label: translate('Grupos de Despacho') || 'Grupos de Despacho',
      value: '',
      size: 12,
    },
    ...buildDispatchGroupColumns(categorizedDispatchGroups, translate),
  ]
}

export function buildActivateDeactivateFields(
  details: UserDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const target = extractTargetUser(details, false)
  const userLabel = extractUserLabel(details, target)
  const registration =
    target?.registration ?? details?.registration ?? 'Não informado'

  return [
    ...buildBaseUserFields(details, translate),
    { label: translate('Usuário-alvo') || 'Usuário-alvo', value: userLabel },
    { label: translate('Matrícula') || 'Matrícula', value: registration },
  ]
}

export function buildChangePasswordFields(
  details: UserDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const target = extractTargetUser(details, true)
  const userLabel = extractUserLabel(details, target)
  const registration =
    target?.registration ?? details?.registration ?? 'Não informado'

  return [
    ...buildBaseUserFields(details, translate),
    { label: translate('Usuário-alvo') || 'Usuário-alvo', value: userLabel },
    { label: translate('Matrícula') || 'Matrícula', value: registration },
  ]
}

export function buildDisconnectFields(
  details: UserDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  const target = extractTargetUser(details, true)
  const userLabel = extractUserLabel(details, target)
  const registration =
    target?.registration ?? details?.registration ?? 'Não informado'

  return [
    ...buildBaseUserFields(details, translate),
    { label: translate('Usuário-alvo') || 'Usuário-alvo', value: userLabel },
    { label: translate('Matrícula') || 'Matrícula', value: registration },
  ]
}
